import { CareEnvironment, CarePhase } from '../../../../appModel';
import {
  calcCareMattersDeathBenefit,
  calcRiderMonthly,
  calcTotalLtcBenefitOverPeriod,
} from '../../calcCareMattersCoverage';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  getBenefitPeriodMonths,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
  getPhaseCareMonthsByPhase,
  getPhasePolicyStartYear,
  getRiderForm,
  getWaitingPeriodBucketForEnvironment,
  resolveRowPolicyYear,
} from '../sharedPolicyConfig';
import { applyIndemnitySurplus } from '../sharedSurplus';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import { Claimant, JointWaitingPeriod, seedJointWaitingPeriod } from '../jointClaimant';

// ---------------------------------------------------------------------------
// CM II strategy state + deltas.
//
// `strategyState` carries the per-claim/per-month state that's specific to
// CareMatters II — the indemnity-surplus reserve and the phase-scoped paid-
// months counter (which walks the rider's policy-year clock). `strategyDeltas`
// carries the per-month flows that are also CM II–specific: the base/rider
// split of the monthly payout and the EP retroactive lump line items.
// ---------------------------------------------------------------------------

// Per-claimant CM II rider clock + EP latch — used per spouse on joint policies.
export interface CmTwoClaimantState {
  // Phase-scoped counter — count of paying months in the current phase.
  // Resets to 0 at monthsIntoPhase === 0. Walks the rider's policy-year clock
  // by paid months (legacy `simulateCareMattersPhase` semantics — the rider
  // does not advance on non-paying months).
  phasePaidMonthsSoFar: number;
  // Whether the retroactive EP reimbursement lump has already fired. The EP is
  // reimbursed ONCE per claim — not once per care-environment WP bucket (an
  // asymmetric policy, e.g. home 60 / facility 90, must not fire it twice).
  epReimbursed: boolean;
}

export interface CmTwoState {
  // Shared across both spouses on a joint policy (legacy parity — A1).
  indemnitySurplus: number;
  // Single-life rider clock + EP latch (flat, bit-identical to pre-joint). On
  // joint rows these mirror the active claimant so the UI/glossary reads stay
  // meaningful; the `joint` sub-object is the source of truth (A9).
  phasePaidMonthsSoFar: number;
  epReimbursed: boolean;
  // Joint only: per-claimant rider clock + EP latch.
  joint?: { client: CmTwoClaimantState; partner: CmTwoClaimantState };
  // Joint only: per-claimant waiting-period balances (each spouse serves their
  // own EP). Single-life uses the universal MonthlyState.waitingPeriod slot.
  jointWaitingPeriod?: JointWaitingPeriod;
}

export interface CmTwoDeltas {
  baseBenefitPaid: number;
  riderBenefitPaid: number;
  baseBenefitDays: number;
  riderBenefitDays: number;
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
  // Total dollars paid out by the EP retroactive WP lump this month (base
  // portion + rider portion). Used to compute cumulative payout and the
  // "WP reimbursement" UI line.
  epLumpSumPaid: number;
  // Base-pool-depleting portion of the EP lump (days × baseDaily). Only
  // this portion reduces the base pool / deathBenefit — the rider portion
  // is paid on top, mirroring the monthly base+rider model.
  epLumpSumBasePaid: number;
  epLumpSumDays: number;
  epLumpSumDailyRate: number;
}

// CM II's retroactive WP lump. Fires on the first month where home OR
// facility WP transitions from positive → 0. The lump pays
// `daysServed × (baseDaily + riderDaily)` total; only the base portion
// (`daysServed × baseDaily`) depletes the base pool, matching CM II's
// regular monthly pay model where the rider sits on top of the pool.
// Cumulative days-served is reconstructed from initial − current on
// whichever pool started larger (both decrement equally per month, so the
// larger pool is the canonical counter; the smaller hits 0 first and stops
// contributing). When the pool can't cover the full served-days at base
// daily, the lump scales proportionally — fewer days are funded and both
// base and rider stop together for the unfunded tail.
interface EpLumpInputs {
  ltcPolicy: PolicyFundingSource;
  careEnvironment: CareEnvironment;
  // The active claimant's WP balance before this row (single-life: the
  // universal slot; joint: the active spouse's per-claimant balance).
  waitingPeriodBefore: WaitingPeriodState;
  closeHomeWp: number;
  closeFacilityWp: number;
  baseBenefitPoolAfterBase: number;
  baseMonthly: number;
  // Rider monthly at the policy year of WP satisfaction. Year 1 is $0 (the
  // rider only steps up after the first anniversary — see `calcRiderMonthly`).
  riderMonthly: number;
  // Whether the lump has already fired earlier in the claim. The EP is
  // reimbursed once per claim, so a second bucket satisfying later must not
  // fire again.
  alreadyReimbursed: boolean;
}

interface EpLumpResult {
  epLumpSumPaid: number;
  epLumpSumBasePaid: number;
  epLumpSumDays: number;
  epLumpSumDailyRate: number;
  // True on the row where the lump fires (the EP's first satisfaction while
  // retroactive reimbursement is enabled). Flips the per-claim `epReimbursed`
  // latch so it never fires again.
  fired: boolean;
}

function computeEpRetroactiveLump(input: EpLumpInputs): EpLumpResult {
  const {
    ltcPolicy,
    careEnvironment,
    waitingPeriodBefore,
    closeHomeWp,
    closeFacilityWp,
    baseBenefitPoolAfterBase,
    baseMonthly,
    riderMonthly,
    alreadyReimbursed,
  } = input;

  // Fire when the active environment's elimination-period bucket is fully
  // satisfied for this month — but only once per claim (per claimant on joint).
  // Whichever environment's WP satisfies first triggers it; a later bucket
  // satisfaction must not fire it again.
  const wpBucket = getWaitingPeriodBucketForEnvironment(careEnvironment);
  const openRelevantWp =
    wpBucket === 'homeDays' ? waitingPeriodBefore.homeDays : waitingPeriodBefore.facilityDays;
  const closeRelevantWp = wpBucket === 'homeDays' ? closeHomeWp : closeFacilityWp;
  const epJustSatisfied = openRelevantWp > 0 && closeRelevantWp === 0;
  const epEligible =
    ltcPolicy.hasRetroactiveWaitingPeriodReimbursement === true &&
    epJustSatisfied &&
    !alreadyReimbursed;

  if (!epEligible) {
    return {
      epLumpSumPaid: 0,
      epLumpSumBasePaid: 0,
      epLumpSumDays: 0,
      epLumpSumDailyRate: 0,
      fired: false,
    };
  }

  // Mirror the same Remaining ?? Days ?? 0 fallback chain that initClaimState
  // uses to seed the WP balance. For a not-yet-on-claim policy `…Remaining`
  // is null but `…Days` carries the configured WP — without the fallback
  // here, cumulativeWpDaysServed evaluates to 0 and the lump silently zeros.
  const initialHomeWp =
    ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
    ltcPolicy.homeCareWaitingPeriodDays ??
    0;
  const initialFacilityWp =
    ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
    ltcPolicy.facilityCareWaitingPeriodDays ??
    0;
  const initialRelevantWp = wpBucket === 'homeDays' ? initialHomeWp : initialFacilityWp;
  const cumulativeWpDaysServed = Math.max(initialRelevantWp - closeRelevantWp, 0);

  const baseDaily = baseMonthly / DAYS_IN_MONTH;
  const riderDaily = riderMonthly / DAYS_IN_MONTH;
  const inflatedDaily = baseDaily + riderDaily;

  // Cap by base pool: pool can fund at most `pool / baseDaily` days at the
  // base rate. Rider scales together with base — both stop on the same day.
  const maxDaysFundable = baseDaily > 0 ? baseBenefitPoolAfterBase / baseDaily : 0;
  const daysFunded = Math.min(cumulativeWpDaysServed, Math.max(maxDaysFundable, 0));
  const epLumpSumBasePaid = daysFunded * baseDaily;
  const epLumpSumPaid = daysFunded * inflatedDaily;

  return {
    epLumpSumPaid,
    epLumpSumBasePaid,
    epLumpSumDays: daysFunded,
    epLumpSumDailyRate: inflatedDaily,
    fired: true,
  };
}

// CM-II specific: rider walks paid months (not calendar months). Other
// products that key their rider off calendar time should NOT use this
// helper — it's intentionally local to the CM strategy.
function policyYearForPayingRow(
  phasePolicyStartYear: number,
  paidMonthsSoFarInPhase: number,
): number {
  const phaseStartYearFloor = Math.floor(phasePolicyStartYear);
  // Match the simulator's epsilon-floor to avoid an early year-bump from
  // floating-point fuzz at near-anniversary fractional inputs.
  const monthsIntoStartYear = Math.floor(
    (phasePolicyStartYear - phaseStartYearFloor) * 12 + 1e-6,
  );
  const monthsFromYearStart = monthsIntoStartYear + paidMonthsSoFarInPhase;
  return phaseStartYearFloor + Math.floor(monthsFromYearStart / 12);
}

export const cmTwoStrategy: PolicyProjectionStrategy<CmTwoState, CmTwoDeltas> = {
  initClaimState(_client, ltcPolicy, jointAnchorPolicyYear): MonthlyState<CmTwoState> {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const benefitPeriodMonths = getBenefitPeriodMonths(ltcPolicy);
    const totalBasePool = baseMonthly * benefitPeriodMonths;
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const basePool = Math.max(totalBasePool - utilizedToDate, 0);
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;

    // `…WaitingPeriodDaysRemaining` is the on-claim runtime counter — set only
    // after the client has begun a claim. For a projection of a not-yet-on-claim
    // policy it is null, so fall back to the configured WP days; without this
    // the projection would pay from month 1 instead of serving the EP first.
    const waitingPeriod: WaitingPeriodState = {
      homeDays:
        ltcPolicy.homeCareWaitingPeriodDaysRemaining ?? ltcPolicy.homeCareWaitingPeriodDays ?? 0,
      facilityDays:
        ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
        ltcPolicy.facilityCareWaitingPeriodDays ??
        0,
    };

    // CM II's base pool is flat (no anniversary inflation), so the anchor is
    // only used to detect a joint run and seed per-claimant state.
    const isJoint = jointAnchorPolicyYear != null;
    return {
      pools: {
        baseBenefitPool: basePool,
        extensionOfBenefitsPool: 0,
        deathBenefit: Math.max(faceAmount - utilizedToDate, minGuaranteed),
      },
      waitingPeriod,
      strategyState: isJoint
        ? {
            indemnitySurplus: 0,
            phasePaidMonthsSoFar: 0,
            epReimbursed: false,
            joint: {
              client: { phasePaidMonthsSoFar: 0, epReimbursed: false },
              partner: { phasePaidMonthsSoFar: 0, epReimbursed: false },
            },
            jointWaitingPeriod: seedJointWaitingPeriod(waitingPeriod),
          }
        : { indemnitySurplus: 0, phasePaidMonthsSoFar: 0, epReimbursed: false },
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const riderForm = getRiderForm(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const policyPremiumStartYear = ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear;
    const firstPhaseStartYear = getFirstContributingPhaseStartYear(clientAsClient);
    // Lifetime LTC benefit (full-utilization, rider-aware), matching the legacy
    // `calcPolicySummaryStats` source. CM II's per-year snapshot
    // `effectiveTotalLtcBenefit` is a MONTHLY figure, so the adapter would
    // otherwise surface one month's benefit as the policy's total LTC benefit.
    // Claim starts in the policy year of the first contributing phase (matches
    // the projection's first claiming row); pre-policy onsets floor at year 1.
    const claimStartPolicyYear = Math.max(1, firstPhaseStartYear - policyPremiumStartYear + 1);
    const lifetimeLtcBenefit = calcTotalLtcBenefitOverPeriod({
      baseMonthly,
      benefitPeriodMonths: getBenefitPeriodMonths(ltcPolicy),
      claimStartPolicyYear,
      riderForm,
    });
    return {
      strategyId: 'cm-two',
      lifetimeLtcBenefit,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Resolve every environment through the same helper the payout math
      // uses (getMonthlyBenefitAmountForEnvironment → getDailyBenefitAmountForEnvironment).
      // A raw `?? 0` here would turn a sparse/unpopulated env field into a hard
      // $0 that the presentation's `?? nominalDailyBenefitBase` can't rescue (0
      // isn't nullish), so the table would show a $0 cap while the math paid at
      // the resolved max. The helper honors an explicit 0 ("not covered") and
      // falls back to the max populated daily for sparse imports.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(ltcPolicy, CareEnvironment.home),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      inflationRate: riderForm.rate,
      inflationCapYears: riderForm.capYears ?? 0,
      inflationIsSimple: riderForm.simple,
      // baseBenefitPool / extensionOfBenefitsPool inflation knobs are
      // intentionally omitted — CM II's base pool is flat (no per-pool
      // inflation concept; rider escalation lives on `inflationRate`
      // above), and CM II has no EOB pool.
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear,
      policyPremiumStartYear,
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      isReimbursement: false,
      isIndemnity: true,
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear, calendarYearOffset } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const riderForm = getRiderForm(ltcPolicy);
    const riderMonthly = calcRiderMonthly(baseMonthly, policyYear, riderForm);
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0; // CM II flat
    const totalMonthlyBenefit = baseMonthly + riderMonthly;
    // CM II's base benefit pool is flat — it does not inflate (the rider
    // grows the monthly benefit instead). Its theoretical lifetime ceiling is
    // the constant base-monthly × benefit-period months.
    const benefitPeriodMonths = getBenefitPeriodMonths(ltcPolicy);
    return {
      policyYear,
      calendarYearOffset,
      effectiveDailyBenefit: dailyBenefit + riderMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: dailyBenefit,
      effectiveDailyBenefitRider: riderMonthly / DAYS_IN_MONTH,
      effectiveFaceAmount: faceAmount,
      effectiveContinuationBenefit: 0,
      effectiveTotalLtcBenefit: totalMonthlyBenefit,
      monthlyBenefitCap: totalMonthlyBenefit,
      theoreticalLtcPoolCeiling: baseMonthly * benefitPeriodMonths,
    };
  },

  computeMonth(
    input: MonthComputeInput<CmTwoState, CmTwoDeltas>,
  ): MonthlyEntry<CmTwoState, CmTwoDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorClaimantMonth } =
      input;
    const { monthIndex, carePhase, monthsIntoPhase, claimant } = monthlyCareInput;
    const clientAsClient = client as Client;

    // Joint-ness is read from the carried state (per-claimant maps present), not
    // the row tag, so a joint run always preserves both spouses' state.
    const jointBefore = openState.strategyState.joint;
    const jointWaitingPeriodBefore = openState.strategyState.jointWaitingPeriod;
    const activeClaimant: Claimant = claimant ?? 'client';
    // The active claimant's rider clock + EP latch before this row.
    const claimantBefore: CmTwoClaimantState = jointBefore
      ? jointBefore[activeClaimant]
      : {
          phasePaidMonthsSoFar: openState.strategyState.phasePaidMonthsSoFar,
          epReimbursed: openState.strategyState.epReimbursed,
        };

    // Each spouse serves their own EP: joint reads the active claimant's balance
    // from strategyState (the universal waitingPeriod slot is rewritten per row).
    const waitingPeriodBefore: WaitingPeriodState = jointWaitingPeriodBefore
      ? jointWaitingPeriodBefore[activeClaimant]
      : openState.waitingPeriod;

    // The row's openState surfaces the active claimant's WP-before so identity
    // checks + UI read per-claimant. Shared pools + surplus pass through.
    const rowOpenState: MonthlyState<CmTwoState> = jointWaitingPeriodBefore
      ? { ...openState, waitingPeriod: waitingPeriodBefore }
      : openState;

    // Per-month effective care cost (annual-stepped from today, anchored at the
    // phase's predicted start year). Needed up front for the pre-policy gate.
    const costMetrics = computeMonthlyCostMetrics(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );

    // STRUCTURAL policy-year clock for the row (single monotonic calendar clock
    // — see the riderPolicyYear/displayPolicyYear note below). Resolved early so
    // the pre-policy gate can run before any WP/ADL classification.
    const displayPolicyYear = resolveRowPolicyYear(
      clientAsClient,
      monthIndex,
      input.jointAnchorPolicyYear,
    );

    const context: MonthContext = {
      monthIndex,
      policyYear: displayPolicyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, displayPolicyYear),
      calendarYearOffset: Math.floor(monthIndex / 12),
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      claimant: monthlyCareInput.claimant,
    };

    // Pre-policy gate: a policy cannot pay for care delivered before its
    // premium-start year (policy year < 1). Book the full cost out-of-pocket
    // and carry every balance forward untouched — no pool drawdown, no EP
    // consumption, no DB reduction. Placed before WP/ADL classification and all
    // payout math. (Single-life floors policy year at 1 upstream, so only joint
    // partner rows can reach here.)
    if (displayPolicyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<CmTwoState, CmTwoDeltas>({
        context,
        facts: prePolicyFacts,
        openState: rowOpenState,
        strategyDeltas: {
          baseBenefitPaid: 0,
          riderBenefitPaid: 0,
          baseBenefitDays: 0,
          riderBenefitDays: 0,
          indemnitySurplusAccrued: 0,
          indemnitySurplusApplied: 0,
          epLumpSumPaid: 0,
          epLumpSumBasePaid: 0,
          epLumpSumDays: 0,
          epLumpSumDailyRate: 0,
        },
      });
    }

    // 1. Day classification:
    //    a. 2-ADL bucket (first daysInPhase * 0.5 days, when applicable)
    //    b. Waiting period (consume the active claimant's EP balance)
    //    c. Remaining days are eligible
    const non2AdlDaysThisRow = computeNon2AdlDaysThisRow(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDaysThisRow;

    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      waitingPeriodBefore,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;

    const eligibleDaysThisRow = daysAfter2Adl - wpDaysThisRow;

    // 2. Paid-month counter (phase-scoped, per active claimant).
    const paidSoFar = monthsIntoPhase === 0 ? 0 : claimantBefore.phasePaidMonthsSoFar;

    // 3. Per-row dollar math.
    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    const riderForm = getRiderForm(ltcPolicy);
    const phasePolicyStartYear = getPhasePolicyStartYear(clientAsClient, phaseCarePlan);

    // CM II rule: only full-month rows pay, and only when the base pool can
    // cover a full month's benefit. Partial final months strand the sub-
    // monthly remainder in the pool (matches legacy `floor(pool/baseMonthly)`
    // — carriers don't cut a partial indemnity check).
    const canAffordFullMonth = openState.pools.baseBenefitPool + 1e-6 >= baseMonthly;
    const isPayingRow = eligibleDaysThisRow === DAYS_IN_MONTH && canAffordFullMonth;

    // Two distinct policy-year clocks, intentionally separated:
    //
    // 1. `riderPolicyYear` — a DOLLAR-ONLY clock for rider escalation. The CM
    //    II rider steps on PAID months (it pauses during the waiting period),
    //    so paying rows walk paid months from the phase start. Non-paying rows
    //    keep the calendar year so an EP retroactive lump on a WP-satisfying
    //    row escalates consistently. This must NOT feed `context.policyYear`.
    //
    // 2. `displayPolicyYear` — the STRUCTURAL clock stored on the row. A single
    //    monotonic calendar clock for every row (paying or not). Using one
    //    formula for all rows is what keeps the year from reversing across the
    //    paying<->non-paying boundary (the old code's 28->27->29 sawtooth) and
    //    keeps anniversaries, year-grouping, snapshot dedup, and age monotonic.
    const riderPolicyYear = isPayingRow
      ? policyYearForPayingRow(phasePolicyStartYear, paidSoFar)
      : displayPolicyYear;

    let basePay = 0;
    let riderPay = 0;
    if (isPayingRow) {
      basePay = baseMonthly;
      riderPay = calcRiderMonthly(baseMonthly, riderPolicyYear, riderForm);
    }
    const indemnityPayout = basePay + riderPay;

    // 4. Per-month effective care cost was resolved up front (`costMetrics`)
    //    for the pre-policy gate; here we only need the eligible-day slice.
    const monthlyBenefitEligibleCost = costMetrics.dailyCareCost * eligibleDaysThisRow;

    // 5. Indemnity-vs-cost surplus accounting (per-row).
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout,
      eligibleCost: monthlyBenefitEligibleCost,
      fullCost: costMetrics.effectiveCareCost,
    });

    // 6. Pool depletion.
    const baseBenefitPoolAfterBase = Math.max(openState.pools.baseBenefitPool - basePay, 0);

    // 7. EP retroactive lump. The reimbursement applies the rider step at
    //    the policy year of WP satisfaction — total paid = days × (base +
    //    rider) daily, but only the base portion depletes the base pool.
    const lumpRiderMonthly = calcRiderMonthly(baseMonthly, riderPolicyYear, riderForm);
    const epResult = computeEpRetroactiveLump({
      ltcPolicy,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      waitingPeriodBefore,
      closeHomeWp,
      closeFacilityWp,
      baseBenefitPoolAfterBase,
      baseMonthly,
      riderMonthly: lumpRiderMonthly,
      alreadyReimbursed: claimantBefore.epReimbursed,
    });

    // 8. Closing balances. Only the base portion of the lump depletes the
    //    pool / death benefit; the rider portion is paid on top (matches CM
    //    II's regular monthly base+rider split where rider does not draw
    //    from the base pool).
    const closeBaseBenefitPool = Math.max(
      baseBenefitPoolAfterBase - epResult.epLumpSumBasePaid,
      0,
    );
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const dbRawDrawdown = basePay + epResult.epLumpSumBasePaid;
    const closeDeathBenefit = Math.max(
      openState.pools.deathBenefit - dbRawDrawdown,
      minGuaranteed,
    );
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const deathBenefitFloored =
      closeDeathBenefit === minGuaranteed && dbRawDrawdown > deathBenefitReduction;

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays: non2AdlDaysThisRow,
      coverageEligibleDays: eligibleDaysThisRow,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      // CM II's per-row benefit ceiling is base + rider at the row's rider
      // clock (paid-month clock on paying rows — it pauses during the WP —
      // calendar clock otherwise). `lumpRiderMonthly` is calcRiderMonthly at
      // exactly that clock.
      appliedMonthlyBenefitCap: baseMonthly + lumpRiderMonthly,
      appliedCapPoolSource: 'base',
    };

    const ineligibleCost =
      costMetrics.effectiveCareCost -
      surplusResult.ltcPayoutToCost -
      epResult.epLumpSumPaid;

    const payout: PayoutSummary = {
      ltcCoveragePaid: surplusResult.ltcCoveragePaid,
      ltcPayoutToCost: surplusResult.ltcPayoutToCost,
      ineligibleCost,
      indemnityPayout,
    };

    const deltas: MonthlyDeltas<CmTwoDeltas> = {
      payout,
      // CM II's base pool drains by the regular monthly base benefit plus
      // the base portion of any EP retroactive lump on the same row. The
      // rider portion is paid on top — it does not deplete the pool.
      base: {
        depletion: basePay + epResult.epLumpSumBasePaid,
        inflationAccrual: 0,
      },
      eob: { depletion: 0, inflationAccrual: 0 },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {
        baseBenefitPaid: basePay,
        riderBenefitPaid: riderPay,
        baseBenefitDays: isPayingRow ? DAYS_IN_MONTH : 0,
        riderBenefitDays: isPayingRow ? DAYS_IN_MONTH : 0,
        indemnitySurplusAccrued: surplusResult.surplusAccrued,
        indemnitySurplusApplied: surplusResult.surplusApplied,
        epLumpSumPaid: epResult.epLumpSumPaid,
        epLumpSumBasePaid: epResult.epLumpSumBasePaid,
        epLumpSumDays: epResult.epLumpSumDays,
        epLumpSumDailyRate: epResult.epLumpSumDailyRate,
      },
    };

    const claimantAfter: CmTwoClaimantState = {
      phasePaidMonthsSoFar: paidSoFar + (isPayingRow ? 1 : 0),
      epReimbursed: claimantBefore.epReimbursed || epResult.fired,
    };
    const activeWaitingPeriodAfter: WaitingPeriodState = {
      homeDays: closeHomeWp,
      facilityDays: closeFacilityWp,
    };

    // indemnitySurplus is shared; the flat rider clock + EP latch mirror the
    // active claimant (the `joint` sub-object below is the source of truth — A9).
    const closeStrategyStateBase = {
      indemnitySurplus: surplusResult.closeSurplus,
      phasePaidMonthsSoFar: claimantAfter.phasePaidMonthsSoFar,
      epReimbursed: claimantAfter.epReimbursed,
    };
    // `joint` and `jointWaitingPeriod` are an invariant pair (seeded together in
    // initClaimState). On a joint row the active claimant is updated and the
    // other spouse carries forward unchanged.
    const closeStrategyState: CmTwoState =
      jointBefore && jointWaitingPeriodBefore
        ? {
            ...closeStrategyStateBase,
            joint: { ...jointBefore, [activeClaimant]: claimantAfter },
            jointWaitingPeriod: {
              ...jointWaitingPeriodBefore,
              [activeClaimant]: activeWaitingPeriodAfter,
            },
          }
        : closeStrategyStateBase;

    const closeState: MonthlyState<CmTwoState> = {
      pools: {
        baseBenefitPool: closeBaseBenefitPool,
        extensionOfBenefitsPool: 0,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: activeWaitingPeriodAfter,
      strategyState: closeStrategyState,
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBaseBenefitPool <= 0,
      clientPoolExhausted: closeBaseBenefitPool <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    // Transition milestones (phase/env change, anniversary, claim-start) compare
    // against `priorClaimantMonth` — the prior row of THIS spouse — not the
    // global previous row, which on a joint policy is often the other spouse
    // (the merged stream interleaves them when their care overlaps) and would
    // fire a spurious transition on every interleaved row.
    const milestones = deriveStandardMilestones<CmTwoState, CmTwoDeltas>({
      context,
      facts,
      openState: rowOpenState,
      closeState,
      deltas,
      priorMonth: priorClaimantMonth,
      retroactiveLumpPaid: epResult.epLumpSumPaid,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: riderForm.rate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState: rowOpenState,
      closeState,
      payout,
    };
    const ineligibilityReason = deriveIneligibilityReason<CmTwoState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<CmTwoState>(ineligibilityInput);

    return {
      context,
      facts,
      openState: rowOpenState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  finalizeDeathBenefit({
    ltcPolicy,
    closingState,
  }: FinalizeDeathBenefitInput<CmTwoState>): number {
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const baseDollarsPaid = Math.max(faceAmount - closingState.pools.deathBenefit, 0);
    return calcCareMattersDeathBenefit({
      specifiedAmount: faceAmount,
      baseDollarsPaid,
      storedFloorAmount: ltcPolicy.policyMinimumGuaranteedBenefitAmount,
    });
  },
};
